/**
 * The AI subscriptions a person can connect to cubic Code, one descriptor per provider: what the
 * browser and the server both read about one. Client-safe; `adapters.ts` holds the server side.
 * Adding a provider: `README.md`, "Adding a provider".
 */
import type { AiSubscriptionProvider as StoredAiSubscriptionProvider } from '@/generated/prisma/enums';
import type { IntegrationType } from '@/lib/integrations/settings-pages';

import type { CodeSessionReasoningEffort } from '../inference/catalog';
import type { AiSubscriptionFirewallRefusals } from './policy';

import { CHATGPT_ERRORS } from './providers/chatgpt/errors';

/**
 * The provider ids, as the `AiSubscriptionProvider` Prisma enum stores them. `satisfies` keeps every
 * id stored; `providers.test.ts` keeps every stored value listed.
 */
export const AI_SUBSCRIPTION_PROVIDERS = [
  'chatgpt',
] as const satisfies readonly StoredAiSubscriptionProvider[];

export type AiSubscriptionProvider = (typeof AI_SUBSCRIPTION_PROVIDERS)[number];

/** Whether `value`, such as a route segment or a stored string, names a provider. */
export function isAiSubscriptionProvider(value: string): value is AiSubscriptionProvider {
  return (AI_SUBSCRIPTION_PROVIDERS as readonly string[]).includes(value);
}

/** A model a provider's subscriptions run; `../models.ts` builds the catalog from each provider's list. */
export type AiSubscriptionModelDescriptor = {
  /** The provider's own id for the model, as its API names it. */
  providerModelId: string;
  /** Hide from new-session choices without removing existing sessions' runtime settings. */
  hiddenFromPicker?: boolean;
  reasoningEffort: CodeSessionReasoningEffort;
  /** Whether the model reads images and PDFs, as `CodeSessionModel.acceptsImages`. */
  acceptsImages: boolean;
  /** Whether Fast mode may be turned on for the model; it may always be turned off. */
  fastTier: boolean;
  /**
   * Whether a new session may run the model at any effort, as `CodeSessionModel.offersEveryEffort`;
   * `plan-models.test.ts` holds it to the plan's model list.
   */
  offersEveryEffort: boolean;
};

/**
 * What a turn's error card offers beside its message: the connect dialog, or the provider's usage
 * page.
 */
export type AiSubscriptionErrorAction = 'connect' | 'reconnect' | 'usage' | null;

/** A turn's failure on a subscription, as its error card shows it. */
export type AiSubscriptionErrorCard = {
  message: string;
  action: AiSubscriptionErrorAction;
  /** Whether sending the message again can work before something outside cubic changes. */
  retry: boolean;
};

/**
 * How a provider's own API names a subscription's failures. The neutral `errors.ts` finds the
 * codes in a failed turn's error by `codePrefixes`, and in a session on the provider by
 * `sessionCodes` too, and asks `card` what each means.
 */
export type AiSubscriptionErrorMatcher = {
  /** What the provider's failure codes start with, such as `subscription_sharing_`. */
  codePrefixes: readonly string[];
  /**
   * Whole codes the provider's API shares with other APIs, such as OAuth's `token_revoked`: they
   * name the subscription's failure only in a session that runs on it.
   */
  sessionCodes: readonly string[];
  /**
   * The card for such a code, or null for one the provider's module doesn't know. `body` is the
   * text the code was found in, for what the provider says beside it; `refusals` the provider's
   * firewall refusals, for a code that means one of them.
   */
  card: (
    code: string,
    body: string,
    refusals: AiSubscriptionFirewallRefusals,
  ) => AiSubscriptionErrorCard | null;
};

export type AiSubscriptionProviderDescriptor = {
  /** The words copy uses for the provider. */
  names: {
    /** The subscription as people call it: "ChatGPT plan". */
    plan: string;
    /** Whose sign-in it is: "ChatGPT", as in "Open ChatGPT again". */
    vendor: string;
    /** The tiers that can be connected, as in "your own ChatGPT Plus or Pro plan". */
    tiers: string;
  };
  /** Its page in Settings → Integrations, whose title `INTEGRATION_PAGE_TITLES` holds. */
  integration: IntegrationType;
  /**
   * OpenCode's provider for the subscription's models: a session on one runs its model as
   * `<openCodeProviderId>/<providerModelId>`, or as OpenCode's fast twin of it in Fast mode.
   */
  openCodeProviderId: string;
  /**
   * The models a session can run on the subscription. A new session starts on the first offered one
   * until the person picks another (`aiSubscriptionStartModel`).
   */
  models: readonly AiSubscriptionModelDescriptor[];
  /** Where the provider shows the subscription's usage and when its limits reset. */
  usageUrl: string;
  /** How the provider's own API names a subscription's failures, for a turn's error card. */
  errors: AiSubscriptionErrorMatcher;
};

export type AiSubscriptionProviderNames = AiSubscriptionProviderDescriptor['names'];

export const AI_SUBSCRIPTION_DESCRIPTORS = {
  chatgpt: {
    names: { plan: 'ChatGPT plan', vendor: 'ChatGPT', tiers: 'Plus or Pro' },
    integration: 'chatgpt-plan',
    openCodeProviderId: 'openai',
    models: [
      // The default model on ChatGPT plans.
      {
        providerModelId: 'gpt-6.1-sol',
        reasoningEffort: 'high',
        acceptsImages: true,
        fastTier: true,
        offersEveryEffort: true,
      },
      {
        providerModelId: 'gpt-6-astra',
        reasoningEffort: 'medium',
        acceptsImages: true,
        fastTier: true,
        offersEveryEffort: true,
      },
      {
        providerModelId: 'gpt-6-sol',
        hiddenFromPicker: true,
        reasoningEffort: 'medium',
        acceptsImages: true,
        fastTier: true,
        offersEveryEffort: true,
      },
      {
        providerModelId: 'gpt-6-luna',
        reasoningEffort: 'max',
        acceptsImages: true,
        fastTier: true,
        offersEveryEffort: true,
      },
      {
        providerModelId: 'gpt-5.6-sol',
        hiddenFromPicker: true,
        reasoningEffort: 'medium',
        acceptsImages: true,
        fastTier: true,
        offersEveryEffort: true,
      },
      {
        providerModelId: 'gpt-5.6-terra',
        hiddenFromPicker: true,
        reasoningEffort: 'medium',
        acceptsImages: true,
        fastTier: true,
        offersEveryEffort: true,
      },
      {
        providerModelId: 'gpt-5.6-luna',
        hiddenFromPicker: true,
        reasoningEffort: 'max',
        acceptsImages: true,
        fastTier: true,
        offersEveryEffort: true,
      },
    ],
    usageUrl: 'https://chatgpt.com/settings/usage',
    errors: CHATGPT_ERRORS,
  },
} as const satisfies Record<AiSubscriptionProvider, AiSubscriptionProviderDescriptor>;
